import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PrimitiveType, PRIMITIVE_ORDER, PRIMITIVE_DEFINITIONS, ProgressionState } from '../types/progression';

interface ProgressionStore extends ProgressionState {
  unlockPrimitive: (primitive: PrimitiveType) => void;
  updateScore: (primitive: PrimitiveType, score: number) => void;
  setCurrentPrimitive: (primitive: PrimitiveType | null) => void;
  checkLevel2Unlock: () => void;
  incrementLevel2Completion: () => void;
  resetProgression: () => void;
  isActionUnlocked: (actionType: string) => boolean;
}

const INITIAL_STATE: ProgressionState = {
  unlockedPrimitives: ['OPSEC'], // Start with only OPSEC unlocked
  primitiveScores: {
    'OPSEC': 0,
    'PublicAffairs': 0,
    'Intel': 0,
    'EW': 0,
    'TechnicalActivities': 0,
    'DeceptiveActivities': 0,
    'PsyOp': 0
  },
  currentPrimitive: null,
  level2Completions: 0,
  isLevel2Unlocked: false,
  isLevel3Unlocked: false
};

export const useProgressionStore = create<ProgressionStore>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      unlockPrimitive: (primitive) => {
        set((state) => {
          if (state.unlockedPrimitives.includes(primitive)) return state;
          return { unlockedPrimitives: [...state.unlockedPrimitives, primitive] };
        });
      },

      updateScore: (primitive, score) => {
        set((state) => {
          const newScores = { ...state.primitiveScores, [primitive]: score };
          
          // Check if next primitive should be unlocked
          const def = PRIMITIVE_DEFINITIONS[primitive];
          if (score >= def.requiredScore) {
            const currentIndex = PRIMITIVE_ORDER.indexOf(primitive);
            if (currentIndex < PRIMITIVE_ORDER.length - 1) {
              const nextPrimitive = PRIMITIVE_ORDER[currentIndex + 1];
              if (!state.unlockedPrimitives.includes(nextPrimitive)) {
                return { 
                  primitiveScores: newScores,
                  unlockedPrimitives: [...state.unlockedPrimitives, nextPrimitive]
                };
              }
            }
          }
          return { primitiveScores: newScores };
        });
        get().checkLevel2Unlock();
      },

      setCurrentPrimitive: (primitive) => {
        set({ currentPrimitive: primitive });
      },

      checkLevel2Unlock: () => {
        const { primitiveScores } = get();
        const allPassed = PRIMITIVE_ORDER.every(p => primitiveScores[p] >= PRIMITIVE_DEFINITIONS[p].requiredScore);
        if (allPassed) {
          set({ isLevel2Unlocked: true });
        }
      },

      incrementLevel2Completion: () => {
        set((state) => {
          const newCount = (state.level2Completions || 0) + 1;
          if (newCount >= 3) {
            return { level2Completions: newCount, isLevel3Unlocked: true };
          }
          return { level2Completions: newCount };
        });
      },

      resetProgression: () => {
        set(INITIAL_STATE);
      },

      isActionUnlocked: (actionType: string) => {
        const { unlockedPrimitives, isLevel2Unlocked, isLevel3Unlocked } = get();
        
        // If Level 3 is unlocked, everything is allowed
        if (isLevel3Unlocked) return true;

        // Find which primitive owns this action
        const owningPrimitive = Object.values(PRIMITIVE_DEFINITIONS).find(def => 
          def.associatedActions.includes(actionType)
        );

        // If no primitive owns it (e.g. generic actions), allow it
        if (!owningPrimitive) return true;

        // Check if the primitive is unlocked
        return unlockedPrimitives.includes(owningPrimitive.id);
      }
    }),
    {
      name: 'progression-storage',
    }
  )
);
